import * as api from '../../../../api';

import * as yup from 'yup';

import { isNumberValid } from '../../../../utils/phoneNumber';

type BaseEditEmergencyContact = api.Previsit.PatientEmergencyContact & {
  isMobile: boolean;
  isHome: boolean;
};

export type AddEmergencyContactValues = Omit<
  BaseEditEmergencyContact,
  'emergencyContactRelationshipToPatient'
> & {
  emergencyContactRelationshipToPatient: '';
};

export type EditEmergencyContactValues = BaseEditEmergencyContact;

type EditEmergencyContactSchemaValues =
  | AddEmergencyContactValues
  | BaseEditEmergencyContact;

export const EditEmergencyContactSchema = (
  generateErrorMessage: (errorType: string, intlKey: string) => string,
) => {
  return yup.lazy((values: EditEmergencyContactSchemaValues) => {
    return yup.object().shape(
      {
        firstName: yup
          .string()
          .min(1)
          .required(
            generateErrorMessage(
              'emergencyContactModalRequiredFieldError',
              'firstName',
            ),
          ),
        lastName: yup
          .string()
          .min(1)
          .required(
            generateErrorMessage(
              'emergencyContactModalRequiredFieldError',
              'lastName',
            ),
          ),
        emergencyContactRelationshipToPatient: yup
          .string()
          .min(1)
          .oneOf(Object.keys(api.Previsit.relationshipMap))
          .required(
            generateErrorMessage(
              'emergencyContactModalRequiredFieldError',
              'relationshipToPatient',
            ),
          ),
        status: yup
          .string()
          .min(1)
          .required(
            generateErrorMessage(
              'emergencyContactModalRequiredFieldError',
              'status',
            ),
          ),
        rank: yup
          .number()
          .required(
            generateErrorMessage(
              'emergencyContactModalRequiredFieldError',
              'rank',
            ),
          ),
        homePhoneNumber: yup.string().when(['cellPhoneNumber', 'isHome'], {
          is: (cellPhoneNumber: string | null, isHome: boolean) =>
            [undefined, null, ''].includes(cellPhoneNumber) || isHome,
          then: yup
            .string()
            .test(
              'is-valid',
              generateErrorMessage(
                'emergencyContactModalInvalidFieldError',
                'phoneNumber',
              ),
              () => isNumberValid(values.homePhoneNumber ?? ''),
            )
            .required(
              generateErrorMessage(
                'emergencyContactModalRequiredFieldError',
                'homePhone',
              ),
            )
            .nullable(),

          otherwise: yup.string().notRequired().nullable(),
        }),
        cellPhoneNumber: yup.string().when(['homePhoneNumber', 'isMobile'], {
          is: (homePhoneNumber: string | null, isMobile: boolean) =>
            [undefined, null, ''].includes(homePhoneNumber) || isMobile,
          then: yup
            .string()
            .test(
              'is-valid',
              generateErrorMessage(
                'emergencyContactModalInvalidFieldError',
                'phoneNumber',
              ),
              () => isNumberValid(values.cellPhoneNumber ?? ''),
            )
            .required(
              generateErrorMessage(
                'emergencyContactModalRequiredFieldError',
                'mobilePhone',
              ),
            )
            .nullable(),
          otherwise: yup.string().notRequired().nullable(),
        }),
        isMobile: yup
          .boolean()
          .required(
            generateErrorMessage(
              'emergencyContactModalRequiredFieldError',
              'phoneType',
            ),
          ),
        isHome: yup
          .boolean()
          .required(
            generateErrorMessage(
              'emergencyContactModalRequiredFieldError',
              'phoneType',
            ),
          ),
      },
      [['homePhoneNumber', 'cellPhoneNumber']],
    );
  });
};
